import React from 'react';
import PropTypes from 'prop-types';
import { Box, Switch, Typography } from '@mui/material';
import Colors from '../../../library/Colors';
import { useAnchor } from '../search/WebshopConfigSearch';
import { usePermissions } from '../../../library/Permissions';
import { cardKeyForAnchor } from '../../../library/permissionCatalog';

export const LANDING_ACTIVE_ANCHOR = 'cfg-card-pages-landing-active';

/**
 * The toolbar row above the landing-page block board: the landing page on/off
 * switch (SHOW_LANDING -- off = the storefront's home route redirects to the
 * explorer) on the left, an optional action (e.g. "Bekijk in Opmaak") on the
 * right. One slim row on purpose: a full card for a single switch pushed the
 * block board down for no reason.
 *
 * Registered with the config search under LANDING_ACTIVE_ANCHOR (scrolls here
 * and flashes) and gated like a ConfigCard with that anchor.
 */
const LandingActiveBar = ({ active, onChange, action, theme = 'light', t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);
    const flash = useAnchor(LANDING_ACTIVE_ANCHOR);
    const { can } = usePermissions();
    const allowed = can(cardKeyForAnchor(LANDING_ACTIVE_ANCHOR));
    if (!allowed && !action) return null;
    const isDark = theme === 'dark';

    return (
        <Box
            id={LANDING_ACTIVE_ANCHOR}
            sx={{
                // Two columns that never wrap: the switch + text take what is
                // left, the action keeps its own width on the same line.
                display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: 1.5,
                minHeight: 36, mb: 1.5, mx: -0.75, px: 0.75, borderRadius: 1.5,
                transition: 'box-shadow 0.25s ease',
                boxShadow: flash
                    ? (isDark ? '0 0 0 3px rgba(245, 184, 75, 0.55)' : `0 0 0 3px ${Colors.sft_blue}66`)
                    : 'none',
            }}
        >
            {allowed ? (
                // width/margin reset: styles/main.css has a global
                // `label { width: 100% }`, which pushed the action to a second
                // line (and the bootstrap reset adds a bottom margin).
                <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: 'auto', m: 0, justifySelf: 'start', maxWidth: '100%', cursor: 'pointer' }}>
                    <Switch size="small" checked={!!active} onChange={(e) => onChange(e.target.checked)} sx={{ flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, color: active ? 'text.primary' : 'warning.dark' }}>
                        {active
                            ? tr('cfg_pages_landing_state_on', 'Landing page on')
                            : tr('cfg_pages_landing_state_off', 'Landing page off')}
                    </Typography>
                    {/* the explanation gives way first when the row is tight */}
                    <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {active
                            ? tr('cfg_pages_landing_hint_on', 'Visitors land on this page first.')
                            : tr('cfg_pages_landing_hint_off', 'Visitors go straight to the explorer. The blocks are kept.')}
                    </Typography>
                </Box>
            ) : <span />}
            {action ? <Box sx={{ justifySelf: 'end', whiteSpace: 'nowrap' }}>{action}</Box> : null}
        </Box>
    );
};
LandingActiveBar.propTypes = { active: PropTypes.bool, onChange: PropTypes.func, action: PropTypes.node, theme: PropTypes.string, t: PropTypes.func };

export default LandingActiveBar;
